/**
 * CA 在线申请（用户端）· 模块共享常量与表单逻辑
 *
 * 本文件是 CA 模块共享内容的唯一入口，分四部分：
 *
 * 一、共享常量（CA 类型 / 业务类型 / 校验正则 / 长度与附件上限 / 客户提示 / 模板编码）：
 * 业务类型说明——value 与 name 由后端固定（applyInfo.productList 随 productValue 下发），
 * BIZ_TYPE_OPTIONS 是其本地登记，仅作中文标签兜底与语义查阅；
 * 订单详情/编辑接口直接下发 productValue（字符串）与 caFunctionType（0 申请 / 1 续费），
 * 前端所有业务类型判断一律用 value 数字；后端新增业务类型时，
 * BIZ_TYPE_OPTIONS 与各组件内的条件字段 value 白名单需同步登记。
 *
 * 二、表单共享逻辑（申请页 apply/index.vue 与订单编辑页 order/edit.vue 复用）：
 *  - 字段 key 清单：CFCA_FORM_KEYS / SHCA_FORM_KEYS / CA_FORM_KEYS
 *    （申请页用于预注册响应式；编辑页用于预填回填与提交时的 key 拣选）；
 *  - buildCaFormRules(caType)：两类 CA 的校验规则（同名 key 文案随厂商切换）。
 *    上海CA 条件字段（营业执照 / 法人身份证号+扫描件 / 公章 / 私章）在模板上被 v-if 隐藏时
 *    el-form-item 自动注销、不参与校验，所以规则无需按业务类型动态裁剪；
 *  - 上海CA 条件字段显隐（显示即必填）：按后端固定的 productValue 数字判定，
 *    value 白名单内联在申请页 / 编辑页 / 详情页各自的显示条件里（含注释）。
 *
 * 三、校验规则（buildCaFormRules，见上第二条）
 *
 * 四、订单状态与支付方式字典（订单列表 / 详情 / 编辑 / 申请页 / 确认弹框共用）
 */

/* ------------------------------------------------------------------ *
 * 一、共享常量
 * ------------------------------------------------------------------ */

/** CA 类型 */
export const CA_TYPE = {
  CFCA: 'CFCA',
  SHCA: 'SHCA', // 上海CA
}

/**
 * 业务类型：value 与 name 由后端固定（applyInfo.productList 随 productValue 下发）。
 *   1-申请单位证书+法人证书    2-续费单位证书+法人证书
 *   3-申请单位证书            4-续费单位证书
 *   5-申请法人证书            6-续费法人证书
 *   7-申请单位证书(境外)      8-续费单位证书(境外)
 * 前端所有业务类型判断一律用 value 数字（详情/编辑接口直接下发 productValue，
 * 字符串形态需转数字）；是否续费由接口 caFunctionType（0 申请 / 1 续费）判定。
 */
export const BIZ_TYPE_OPTIONS = [
  { value: 1, label: '申请单位证书+法人证书' },
  { value: 2, label: '续费单位证书+法人证书' },
  { value: 3, label: '申请单位证书' },
  { value: 4, label: '续费单位证书' },
  { value: 5, label: '申请法人证书' },
  { value: 6, label: '续费法人证书' },
  { value: 7, label: '申请单位证书(境外)' },
  { value: 8, label: '续费单位证书(境外)' },
]

/**
 * 校验正则（附件后缀校验由 FileUpload 组件的 accept/filetypes 自行处理，不在此列）
 */
export const REGEXP = {
  // 手机号：11 位数字、1 开头
  mobile: /^1\d{10}$/,
  // 身份证：18 位，末位可为 X（字母限大写）
  idCard: /^\d{17}[\dX]$/,
  // 统一社会信用代码：18 位数字/大写字母，严格国标校验
  licenceCode: /^[0-9A-HJ-NPQRTUWXY]{2}\d{6}[0-9A-HJ-NPQRTUWXY]{10}$/,
  // 邮箱
  email: /^[\w.-]+@[\w-]+(\.[\w-]+)+$/,
}

/** 邮箱长度上限（需求：由 20 位扩展至 100 位） */
export const EMAIL_MAX_LENGTH = 100

/** 单位名称长度上限（上海 CA：50 字） */
export const ORG_NAME_MAX_LENGTH = 200

/** 附件通用大小上限（MB） */
export const FILE_MAX_SIZE = 20

/**
 * 模板文件编码（pageData.templateFileList 的 templateCode，后端配置）：
 * SQS = 授权书（CA申请授权书字段旁的模板下载），SQB = 申请表，
 * DWGZ = 单位公章（单位公章图片字段旁的模板下载，仅上海CA）。
 * 不要再按 fileName 模糊匹配（文件名可能被后台改文案）。
 */
export const TEMPLATE_CODE = {
  AUTH: 'SQS',
  APPLY: 'SQB',
  ORG_SEAL: 'DWGZ',
}

/* ------------------------------------------------------------------ *
 * 二、表单字段 key 清单
 * ------------------------------------------------------------------ */

/** CFCA 表单字段 key（11 项，key 与后端一致） */
export const CFCA_FORM_KEYS = [
  'linkMan',
  'linkTel',
  'identityCode',
  'linkEmail',
  'postAddr',
  'applyOrgName',
  'licenceCode',
  'legalName',
  'legalIdNo',
  'licenceCodeFile',
  'authFileId',
]

/** 上海CA 表单字段 key（含条件字段；key 与后端一致，无需改名映射） */
export const SHCA_FORM_KEYS = [
  'linkMan',
  'identityCode',
  'identityFileId',
  'linkTel',
  'linkEmail',
  'legalName',
  'legalIdNo',
  'legalIdFileId',
  'applyOrgName',
  'licenceCode',
  'licenceCodeFile',
  'orgSealFileId',
  'legalSealFileId',
  'receiverName',
  'receiverMobile',
  'receiverProvince',
  'receiverCity',
  'receiverZone',
  'postAddr',
]

/** 全部字段 key（两类 CA 并集）：Vue 2 给对象新增属性不是响应式的，form 需先按此预注册 */
export const CA_FORM_KEYS = [...new Set([...CFCA_FORM_KEYS, ...SHCA_FORM_KEYS])]

/* ------------------------------------------------------------------ *
 * 三、校验规则
 * ------------------------------------------------------------------ */

/**
 * 生成 element-ui el-form 的 rules 对象（申请页 / 编辑页共用）。
 * linkMan / identityCode / postAddr 是两类 CA 的同名 key（字段标签不同），
 * 提示文案随 caType 切换；厂商各自专有字段只进对应厂商的规则集。
 * 隐藏字段不校验的前提：条件字段的 el-form-item 必须挂 v-if（注销后 element 不参与校验），
 * 不能用 v-show / CSS 隐藏。
 *
 * @param {string} caType CA_TYPE 枚举
 * @param {function} [getReceiverArea] 读取当前 receiverArea 值的 getter（仅上海CA申请页传入）：
 *   级联与详细地址都为空时，只显示 receiverArea 的提示，postAddr 不再重复报错；
 *   不传（如编辑页无级联项）时 postAddr 保持原有的独立必填校验。
 * @returns {object} el-form :rules
 */
export function buildCaFormRules(caType, getReceiverArea) {
  const isShca = caType === CA_TYPE.SHCA
  const rules = {
    // ---- CFCA / 上海CA 共用字段 ----
    linkTel: [
      { required: true, message: '请输入手机号', trigger: 'blur' },
      { pattern: REGEXP.mobile, message: '请输入正确的 11 位手机号', trigger: 'blur' },
    ],
    linkEmail: [
      { required: true, message: '请输入邮箱', trigger: 'blur' },
      { pattern: REGEXP.email, message: '请输入正确的邮箱', trigger: 'blur' },
      { max: EMAIL_MAX_LENGTH, message: `邮箱不能超过 ${EMAIL_MAX_LENGTH} 个字`, trigger: 'blur' },
    ],
    applyOrgName: [
      { required: true, message: '请输入单位名称', trigger: 'blur' },
      { max: ORG_NAME_MAX_LENGTH, message: `单位名称不能超过 ${ORG_NAME_MAX_LENGTH} 个字`, trigger: 'blur' },
    ],
    licenceCode: [
      { required: true, message: '请输入统一社会信用代码', trigger: 'blur' },
      { pattern: REGEXP.licenceCode, message: '请输入 18 位统一社会信用代码', trigger: 'blur' },
    ],
    legalName: [{ required: true, message: '请输入法定代表人姓名', trigger: 'blur' }],
    legalIdNo: [
      { required: true, message: '请输入法定代表人身份证号', trigger: 'blur' },
      { pattern: REGEXP.idCard, message: '请输入 18 位身份证号', trigger: 'blur' },
    ],
    // ---- 同名 key（两类 CA 共用，提示文案随厂商标签不同）----
    linkMan: [
      { required: true, message: isShca ? '请输入经办人姓名' : '请输入被授权经办人姓名', trigger: 'blur' },
    ],
    identityCode: [
      { required: true, message: isShca ? '请输入经办人身份证号' : '请输入身份证号', trigger: 'blur' },
      { pattern: REGEXP.idCard, message: '请输入 18 位身份证号', trigger: 'blur' },
    ],
    postAddr: [
      {
        required: true,
        trigger: 'blur',
        // 收件地区也未选时交由 receiverArea 的提示展示，避免同一行出现两条报错
        validator: (rule, value, callback) => {
          if (value) return callback()
          if (typeof getReceiverArea === 'function') {
            const area = getReceiverArea()
            if (!Array.isArray(area) || area.length === 0) return callback()
          }
          callback(new Error(isShca ? '请输入收件地址' : '请输入CA邮寄地址'))
        },
      },
    ],
  }
  if (isShca) {
    // ---- 上海CA 专有字段 ----
    Object.assign(rules, {
      identityFileId: [{ required: true, message: '请上传经办人身份证扫描件', trigger: 'change' }],
      // 营业执照扫描件：仅「申请单位证书(3) / 续费单位证书(4)」展示，隐藏时 v-if 注销不参与校验
      licenceCodeFile: [{ required: true, message: '请上传营业执照扫描件', trigger: 'change' }],
      legalIdFileId: [{ required: true, message: '请上传法定代表人身份证扫描件', trigger: 'change' }],
      orgSealFileId: [{ required: true, message: '请上传单位公章图片', trigger: 'change' }],
      legalSealFileId: [{ required: true, message: '请上传法人私章/手写签字图片', trigger: 'change' }],
      receiverName: [{ required: true, message: '请输入收件人姓名', trigger: 'blur' }],
      receiverMobile: [
        { required: true, message: '请输入收件人手机号', trigger: 'blur' },
        { pattern: REGEXP.mobile, message: '请输入正确的 11 位手机号', trigger: 'blur' },
      ],
      // 收件地区（省/市/区县级联，值为区划码数组）：编辑页无此表单项，规则自然不参与校验
      receiverArea: [
        { required: true, type: 'array', message: '请选择收件地区（省/市/区县）', trigger: 'change' },
      ],
    })
  } else {
    // ---- CFCA 专有字段 ----
    Object.assign(rules, {
      licenceCodeFile: [{ required: true, message: '请上传营业执照扫描件', trigger: 'change' }],
      authFileId: [{ required: true, message: '请上传CA申请授权书', trigger: 'change' }],
    })
  }
  return rules
}

/* ------------------------------------------------------------------ *
 * 四、订单状态与支付方式字典
 * ------------------------------------------------------------------ */

/**
 * 订单状态码 → 中文名（接口文档 §9.1 数字码状态机）：
 *   10 待付款（提交订单初始状态，电汇/网上支付相同）
 *   15 待电汇确认（电汇订单已上传付款凭证）
 *   20 审核中（网上支付回调成功 / 电汇确认到账）
 *   30 待认证（初审通过，等 CA 厂商认证）
 *   40 认证通过（可安排寄出）
 *   50 已寄出（CA 载体已快递寄出）
 *   60 已完成（签收后终态）
 *   70 已驳回（可补充材料重提回 20）
 *   80 认证驳回（终态）
 *   90 已取消（落取消原因）
 */
export const ORDER_STATUS_TEXT = {
  10: '待付款',
  15: '待电汇确认',
  20: '审核中',
  30: '待认证',
  40: '认证通过',
  50: '已寄出',
  60: '已完成',
  70: '已驳回',
  80: '认证驳回',
  90: '已取消',
}

/**
 * 用户端展示用状态文案覆盖（码值 → 文案）：认证驳回(80) 在用户端一律按「待认证」展示。
 * 上海CA 认证驳回后客户视角仍停留在「待认证」，不暴露驳回态（需求 189769），
 * 列表页也不提供查看驳回原因 / 修改订单入口。
 * 订单列表 / 详情 / 编辑三处状态文案统一走 orderStatusText()，不要直读 orderStatusName。
 */
export const ORDER_STATUS_TEXT_OVERRIDE = {
  80: '待认证',
}

/**
 * 订单状态展示文案：先查覆盖表（80 → 待认证），其余优先后端下发的 orderStatusName，
 * 缺失时回退本地数字码表（orderStatus 库值可能是 number / string 两种形态）。
 * @param {{ orderStatus?: number|string, orderStatusName?: string }} order 订单对象（列表项 / 详情 / 编辑详情）
 * @returns {string} 展示文案，order 为空时返回 '--'
 */
export function orderStatusText(order) {
  const code = String((order && order.orderStatus) || '')
  const override = ORDER_STATUS_TEXT_OVERRIDE[code]
  if (override) return override
  return (order && order.orderStatusName) || ORDER_STATUS_TEXT[code] || code || '--'
}

/**
 * 「我的订单」顶部状态筛选下拉选项（'' = 全部订单占位值）。
 * 选项值 = 后端库值，筛选时原样透传精准匹配（文档 §4.1）；逗号分隔表示多码等值匹配。
 * 待认证含 30 + 认证驳回 80（与 ORDER_STATUS_TEXT_OVERRIDE 的展示口径一致），
 * 已驳回只剩初审驳回 70。
 */
export const ORDER_STATUS_OPTIONS = [
  { value: '', label: '全部订单' },
  { value: '10', label: '待付款' },
  { value: '15,20', label: '审核中' },
  { value: '30,80', label: '待认证' },
  { value: '40', label: '制证中' },
  { value: '50', label: '已寄出' },
  { value: '60', label: '已完成' },
  { value: '70', label: '已驳回' },
  { value: '90', label: '已取消' },
]

/** 支付方式枚举：10=电汇，20=网上支付（与 order/submit 入参 payType 一致） */
export const PAY_TYPE = {
  WIRE: 10, // 电汇
  ONLINE: 20, // 网上支付
}

/** 发票状态码 → 中文名（库值原样返回，前端 String() 后查表；0=待申请开票，2=开票中，3=开票失败，1=已开票） */
export const INVOICE_STATUS_TEXT = {
  0: '待申请',
  1: '已开票',
  2: '开票中',
  3: '开票失败',
}

/** 发票类型枚举：10=增值税普通发票，20=增值税专用发票（申请弹框默认选中专票） */
export const INVOICE_TYPE = {
  GENERAL: '10',
  SPECIAL: '20',
}

/** 发票类型码 → 中文名（查看弹框展示用） */
export const INVOICE_TYPE_TEXT = {
  10: '增值税普通发票',
  20: '增值税专用发票',
}
